import React, { useState, useMemo, useEffect } from "react"
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  MegaphoneIcon,
  UserPlusIcon,
  ArrowPathIcon,
  LinkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from "@heroicons/react/24/outline"
import Button from "~/ui-rtk/components/ui/controls/Button"
import {
  InitiativeType,
  AdChannel,
  MetaObjective,
  type OptimizationGoal as CaOptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"
import {
  INITIATIVE_TYPE_LABELS,
  getInitiativeTypeLabel,
} from "~/ui-rtk/constants/initiative-types"
import {
  useCustomAttributionControllerGetAllModelsQuery,
  CustomAttributionModelWithWeights,
} from "~/ui-rtk/api/customAttributionApi"
import { toCurrency } from "~/ui-rtk/utils/number-utils"
import { CreateModelModal } from "../dashboards/PaidMediaWeighting/components/CreateModelModal"
import {
  calculateModeledRevenueForRow,
  calculateBrandValueBreakdownForRow,
  getCurrentBrandValueBreakdown,
  getBrandValuePeriodsWithWeights,
  hasBrandValueWeights,
  type BrandValueBreakdown,
} from "~/ui-rtk/utils/calculateModeledRevenue"

interface RowData {
  adset_id?: string
  ad_set_id?: string
  campaign_name?: string
  adset_name?: string
  ad_set_name?: string
  ad_name?: string
  media_channel?: string
  objective?: string
  optimization_goal?: string
  initiative_type?: string
  modeled_revenue?: string
  ga4_total_revenue?: string
  purchase_value_1d_engaged_view?: string
  purchases?: string
  purchase_value_1d_view?: string
}

/** How a campaign-level change should propagate to child ad sets. */
export type CascadeMode = "all" | "empty-only"

interface InitiativeTypeChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (applyCampaignLevel?: boolean, cascadeMode?: CascadeMode) => void
  rowData: RowData
  /**
   * Child ad set IDs for a campaign row. When provided, confirming a change on
   * a campaign offers a choice to cascade to all ad sets or only unclassified
   * ones.
   */
  childAdsetIds?: string[]
  newValue: string
  isLoading?: boolean
}

// Initiative type configurations for styling
const INITIATIVE_TYPE_CONFIGS: Record<
  string,
  {
    icon: React.ReactNode
    color: string
    bgColor: string
    borderColor: string
    label: string
  }
> = {
  [InitiativeType.RETENTION]: {
    icon: <HeartIcon className="w-4 h-4" />,
    color: "text-system-success-300",
    bgColor: "bg-system-success-500/10",
    borderColor: "border-system-success-600/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.RETENTION],
  },
  [InitiativeType.DEMAND_GENERATION]: {
    icon: <MegaphoneIcon className="w-4 h-4" />,
    color: "text-system-cyan-200",
    bgColor: "bg-system-cyan-500/10",
    borderColor: "border-system-cyan-500/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION],
  },
  [InitiativeType.DEMAND_GENERATION_2]: {
    icon: <MegaphoneIcon className="w-4 h-4" />,
    color: "text-system-magenta-200",
    bgColor: "bg-system-magenta-500/10",
    borderColor: "border-system-magenta-500/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION_2],
  },
  [InitiativeType.DEMAND_CAPTURE_ACQUISITION]: {
    icon: <UserPlusIcon className="w-4 h-4" />,
    color: "text-system-sea-300",
    bgColor: "bg-system-sea-500/10",
    borderColor: "border-system-sea-600/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_ACQUISITION],
  },
  [InitiativeType.DEMAND_CAPTURE_RETARGETING]: {
    icon: <ArrowPathIcon className="w-4 h-4" />,
    color: "text-system-orange-200",
    bgColor: "bg-system-orange-500/10",
    borderColor: "border-system-orange-500/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_RETARGETING],
  },
  [InitiativeType.DEMAND_CAPTURE_BROAD]: {
    icon: <LinkIcon className="w-4 h-4" />,
    color: "text-system-yellow-200",
    bgColor: "bg-system-yellow-500/10",
    borderColor: "border-system-yellow-500/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_BROAD],
  },
  [InitiativeType.BRANDED_SEARCH]: {
    icon: <MagnifyingGlassIcon className="w-4 h-4" />,
    color: "text-system-indigo-200",
    bgColor: "bg-system-indigo-500/10",
    borderColor: "border-system-indigo-600/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.BRANDED_SEARCH],
  },
  [InitiativeType.NON_BRAND_SEARCH]: {
    icon: <MagnifyingGlassIcon className="w-4 h-4" />,
    color: "text-system-violet-200",
    bgColor: "bg-system-violet-500/10",
    borderColor: "border-system-violet-600/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.NON_BRAND_SEARCH],
  },
  [InitiativeType.PMAX]: {
    icon: <CubeIcon className="w-4 h-4" />,
    color: "text-system-fuchsia-200",
    bgColor: "bg-system-fuchsia-500/10",
    borderColor: "border-system-fuchsia-600/40",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.PMAX],
  },
}

// Utility function for revenue change styling
const getRevenueChangeStyles = (changeAmount: number) =>
  changeAmount > 0
    ? "text-system-success-300 bg-system-success-500/20 border border-system-success-500/30"
    : "text-system-error-300 bg-system-error-500/20 border border-system-error-500/30"

// Helper function to get the correct brand value label
const getBrandValueLabel = (period: string) =>
  period === "180d" ? "Brand Value" : `${period} Brand Value`

/** Read a row's Cube modeled_revenue, handling both the unprefixed key and the
 *  `company_media_metrics_adsets.`-prefixed key emitted by the adsets table. */
const readRowModeledRevenue = (
  rowData: Record<string, unknown> | undefined,
): number =>
  parseFloat(
    (rowData?.modeled_revenue ||
      rowData?.["company_media_metrics_adsets.modeled_revenue"] ||
      "0") as string,
  ) || 0

export function InitiativeTypeChangeModal({
  isOpen,
  onClose,
  onConfirm,
  rowData,
  childAdsetIds,
  newValue,
  isLoading = false,
}: InitiativeTypeChangeModalProps) {
  const [applyCampaignLevel, setApplyCampaignLevel] = useState(false)
  const [cascadeMode, setCascadeMode] = useState<CascadeMode>("all")
  const [showCreateModel, setShowCreateModel] = useState(false)
  const [activeTab, setActiveTab] = useState<"revenue" | "brand-value">(
    "revenue",
  )

  const isAdset = !!(rowData?.adset_id || rowData?.ad_set_id)
  const itemType = isAdset ? "Ad Set" : "Campaign"
  const itemName = rowData?.adset_name || rowData?.ad_set_name || "Unknown"
  const campaignName = rowData?.campaign_name || "Unknown Campaign"
  const isClearing = !newValue || newValue === ""
  // Campaign row with known child ad sets: offer the cascade choice.
  const canCascade = !isAdset && !!childAdsetIds && childAdsetIds.length > 0

  // Helper function to get the scope description for warnings
  const getScopeDescription = (): string => {
    if (isAdset && applyCampaignLevel) {
      return `all ad sets in the "${campaignName}" campaign`
    }
    if (isAdset) {
      return `the "${itemName}" ad set`
    }
    return `the "${campaignName}" campaign`
  }

  // Helper function to get button suffix
  const getButtonSuffix = (): string =>
    isAdset && applyCampaignLevel ? " (Campaign)" : ""

  const handleConfirm = () => {
    if (canCascade) {
      onConfirm(false, cascadeMode)
    } else {
      onConfirm(applyCampaignLevel)
    }
  }

  // Fetch custom attribution models to calculate projected revenue
  const { data: attributionModels = [], refetch: refetchModels } =
    useCustomAttributionControllerGetAllModelsQuery()

  const getAdChannel = (mediaChannel: string): AdChannel | null => {
    // Map media channel to AdChannel enum
    if (mediaChannel?.toLowerCase().includes("meta")) {
      return AdChannel.META
    }
    if (mediaChannel?.toLowerCase().includes("tiktok")) {
      return AdChannel.TIKTOK
    }
    if (mediaChannel?.toLowerCase().includes("pinterest")) {
      return AdChannel.PINTEREST
    }
    if (mediaChannel?.toLowerCase().includes("google")) {
      return AdChannel.GOOGLE_ADS
    }
    if (mediaChannel?.toLowerCase().includes("youtube")) {
      return AdChannel.YOUTUBE
    }
    return null
  }

  // Find matching attribution model for the new initiative type using the same
  // 3-tier fallback the Budget Recommender uses (findModel), so a model is
  // resolved even when the row's objective/optimization goal don't exactly match
  // the model's — common for custom initiative types.
  const projectedModel = useMemo(() => {
    if (isClearing || !newValue || !rowData) return null

    const adChannel = getAdChannel(rowData.media_channel || "")
    if (!adChannel) return null

    const models = attributionModels as CustomAttributionModelWithWeights[]

    // Tier 1: initiative type + channel + objective + optimization goal
    if (rowData.objective || rowData.optimization_goal) {
      const exact = models.find(
        model =>
          String(model.initiativeType) === String(newValue) &&
          model.adChannel === adChannel &&
          (!rowData.objective ||
            String(model.adChannelObjective) === String(rowData.objective)) &&
          (!rowData.optimization_goal ||
            String(model.adChannelOptimizationGoal) ===
              String(rowData.optimization_goal)),
      )
      if (exact) return exact
    }
    // Tier 2: initiative type + channel
    const channelMatch = models.find(
      model =>
        String(model.initiativeType) === String(newValue) &&
        model.adChannel === adChannel,
    )
    if (channelMatch) return channelMatch
    // Tier 3: initiative type only
    return (
      models.find(model => String(model.initiativeType) === String(newValue)) ??
      null
    )
  }, [attributionModels, newValue, isClearing, rowData])

  // Find existing model with exact match for editing
  const existingModelForEdit = useMemo(() => {
    if (isClearing || !newValue || !rowData) return null

    const adChannel = getAdChannel(rowData.media_channel || "")
    if (!adChannel) return null

    const modelsWithWeights =
      attributionModels as CustomAttributionModelWithWeights[]

    // Find exact match including objective and optimization goal
    return modelsWithWeights.find(
      model =>
        String(model.initiativeType) === String(newValue) &&
        model.adChannel === adChannel &&
        String(model.adChannelObjective || "") ===
          String(rowData.objective || "") &&
        String(model.adChannelOptimizationGoal || "") ===
          String(rowData.optimization_goal || ""),
    )
  }, [attributionModels, newValue, isClearing, rowData])

  // Calculate the projected modeled revenue the same way the Budget Recommender
  // does: weight the row's Cube measures with the projected model, falling back
  // to the row's Cube modeled_revenue when the weighted result is 0 (no model,
  // zero weights, or measures the model doesn't reference).
  const calculateModeledRevenue = (): number => {
    if (!rowData) return 0
    const adChannel =
      projectedModel?.adChannel ?? getAdChannel(rowData.media_channel || "")
    const weighted = calculateModeledRevenueForRow({
      rowData: rowData as Record<string, unknown>,
      model: projectedModel ?? undefined,
      adChannel,
    })
    return weighted > 0
      ? weighted
      : readRowModeledRevenue(rowData as Record<string, unknown>)
  }

  // Calculate brand value breakdown using custom weights
  const calculateBrandValueBreakdown = (): BrandValueBreakdown => {
    if (!rowData || !projectedModel) {
      return { "30d": 0, "60d": 0, "90d": 0, "180d": 0, total: 0 }
    }
    return calculateBrandValueBreakdownForRow({
      rowData: rowData as Record<string, unknown>,
      model: projectedModel,
    })
  }

  // Calculate current and projected modeled revenue
  const currentModeledRevenue = useMemo(
    () => readRowModeledRevenue(rowData as Record<string, unknown>),
    [rowData],
  )

  // Calculate current and projected brand value breakdowns
  const currentBrandValueBreakdown = useMemo(
    () => getCurrentBrandValueBreakdown(rowData as Record<string, unknown>),
    [rowData],
  )

  // Get which brand value periods have weights configured
  const brandValuePeriodsWithWeights = useMemo(
    () => getBrandValuePeriodsWithWeights(projectedModel || undefined),
    [projectedModel],
  )

  // Check if the projected model has brand value weights
  const hasBrandValueConfig = useMemo(
    () => hasBrandValueWeights(projectedModel || undefined),
    [projectedModel],
  )

  const handleClose = () => {
    setApplyCampaignLevel(false)
    setCascadeMode("all")
    setActiveTab("revenue") // Reset to revenue tab
    onClose()
  }

  // Auto-switch to brand value tab if only brand value weights are configured
  useEffect(() => {
    if (hasBrandValueConfig && brandValuePeriodsWithWeights.length > 0) {
      // Check if revenue model has any non-brand-value weights
      const hasRevenueWeights = projectedModel?.weights?.some(
        weight =>
          !weight.metric.startsWith("BRAND_VALUE_") && weight.weight > 0,
      )

      // If only brand value weights are configured, switch to brand value tab
      if (!hasRevenueWeights) {
        setActiveTab("brand-value")
      }
    }
  }, [hasBrandValueConfig, brandValuePeriodsWithWeights, projectedModel])

  const projectedModeledRevenue = useMemo(
    () => calculateModeledRevenue(),
    [projectedModel, rowData, existingModelForEdit],
  )

  const projectedBrandValueBreakdown = useMemo(
    () => calculateBrandValueBreakdown(),
    [projectedModel, rowData, existingModelForEdit],
  )

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col">
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-system-blue-800/90 z-50 flex items-center justify-center rounded-system-xl">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-system-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-white font-medium">Saving changes...</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-system-blue-700 border-b border-system-blue-600/50 px-6 py-4 rounded-t-system-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              Confirm Initiative Type Change
            </h2>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-system-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-system-blue-600/20 disabled:opacity-50"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-hidden relative min-h-0">
          <div
            className="h-full overflow-y-auto p-4 space-y-4"
            style={{
              scrollbarGutter: "stable",
            }}
          >
            {/* Campaign/Ad Set Info at Top */}
            <div className="bg-gradient-to-r from-system-blue-600/15 to-system-blue-700/10 rounded-xl p-5">
              {/* Campaign and Ad Set Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white truncate">
                    {campaignName}
                  </h3>
                  <span className="px-2 py-1 bg-system-blue-500/20 text-system-blue-200 text-xs font-medium rounded-md">
                    Campaign
                  </span>
                </div>
                {isAdset && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-system-gray-400">
                      {itemType}:
                    </span>
                    <span className="text-sm text-system-gray-200 font-medium">
                      {itemName}
                    </span>
                    <span className="px-2 py-1 bg-system-gray-600/20 text-system-gray-300 text-xs font-medium rounded-md">
                      Ad Set
                    </span>
                  </div>
                )}
              </div>

              {/* Ad Configuration */}
              {(rowData?.objective || rowData?.optimization_goal) && (
                <div className="mt-5 pt-4 border-t border-system-blue-500/20">
                  <h4 className="text-sm font-semibold text-system-blue-200 uppercase tracking-wide mb-4">
                    Ad Configuration
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {rowData?.objective && (
                      <div className="bg-system-blue-700/20 rounded-lg p-3">
                        <div className="text-xs text-system-blue-300 font-medium uppercase tracking-wide mb-1">
                          Objective
                        </div>
                        <div className="text-sm text-white font-semibold">
                          {rowData.objective.replace(/_/g, " ").toLowerCase()}
                        </div>
                      </div>
                    )}
                    {rowData?.optimization_goal && (
                      <div className="bg-system-blue-700/20 rounded-lg p-3">
                        <div className="text-xs text-system-blue-300 font-medium uppercase tracking-wide mb-1">
                          Optimization Goal
                        </div>
                        <div className="text-sm text-white font-semibold">
                          {rowData.optimization_goal
                            .replace(/_/g, " ")
                            .toLowerCase()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Impact Analysis Header */}
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white mb-1">
                Impact Analysis
              </h4>
              <p className="text-sm text-system-gray-400">
                Review the projected changes before confirming
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-system-blue-600/30 bg-system-blue-600/10">
              <button
                type="button"
                onClick={() => setActiveTab("revenue")}
                className={`flex-1 px-system-md py-system-sm text-base font-medium transition-all ${
                  activeTab === "revenue"
                    ? "text-system-blue-200 border-b-2 border-system-blue-400 bg-system-blue-500/10"
                    : "text-system-gray-400 hover:text-system-blue-200 hover:bg-system-blue-500/5"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Revenue Impact</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("brand-value")}
                disabled={!hasBrandValueConfig}
                className={`flex-1 px-system-md py-system-sm text-base font-medium transition-all ${(() => {
                  if (activeTab === "brand-value" && hasBrandValueConfig) {
                    return "text-basic-pink border-b-2 border-basic-pink bg-basic-pink/10"
                  }
                  if (hasBrandValueConfig) {
                    return "text-system-gray-400 hover:text-basic-pink hover:bg-basic-pink/5"
                  }
                  return "text-system-gray-500 cursor-not-allowed opacity-50"
                })()}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Brand Value Impact</span>
                  {!hasBrandValueConfig && (
                    <span className="text-xs">(N/A)</span>
                  )}
                </div>
              </button>
            </div>

            {/* Model Management - Create/Edit */}
            {!isClearing && newValue && (
              <div className="bg-system-blue-600/10 border border-system-blue-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-system-blue-500/20 rounded-lg flex items-center justify-center">
                    <PlusIcon className="w-5 h-5 text-system-blue-300" />
                  </div>
                  <div className="flex-1">
                    <h5 className="text-sm font-medium text-white mb-1">
                      {existingModelForEdit
                        ? "Edit Attribution Model"
                        : "Attribution Model Required"}
                    </h5>
                    <p className="text-xs text-system-gray-400">
                      {existingModelForEdit
                        ? "Configure weights for the attribution model to get accurate revenue projections."
                        : "No attribution model found for this initiative type and channel combination."}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant={{ variant: "solid", color: "pink" }}
                    onClick={() => setShowCreateModel(true)}
                    className="px-3 py-2 text-sm"
                  >
                    {existingModelForEdit ? "Edit Model" : "Create Model"}
                  </Button>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {activeTab === "revenue" && (
              <div className="space-y-4">
                {/* Revenue Comparison Headers */}
                <div className="flex items-center justify-between">
                  <div className="flex-1 text-center">
                    <h4 className="text-sm font-medium text-system-gray-300">
                      Current Revenue
                    </h4>
                  </div>
                  <div className="w-20"></div> {/* Spacer for chevron */}
                  <div className="flex-1 text-center">
                    <h4 className="text-sm font-medium text-system-gray-300">
                      After Change
                    </h4>
                  </div>
                </div>

                {/* Revenue Comparison */}
                <div className="flex items-center gap-6">
                  {/* Left Side - Current Revenue */}
                  <div className="flex-1 space-y-4">
                    {/* Current Initiative Type */}
                    <div className="bg-system-blue-600/10 rounded-lg p-4 border border-system-blue-600/20 h-20">
                      <div className="text-center space-y-2">
                        <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                          Initiative Type
                        </div>
                        <div className="text-sm font-medium text-white">
                          {rowData?.initiative_type
                            ? getInitiativeTypeLabel(rowData.initiative_type)
                            : "Not Set"}
                        </div>
                      </div>
                    </div>

                    {/* Current Modeled Revenue */}
                    <div className="bg-system-blue-600/10 rounded-lg p-4 border border-system-blue-600/20 h-20">
                      <div className="text-center space-y-2">
                        <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                          Modeled Revenue
                        </div>
                        <div className="text-lg font-bold text-white">
                          {toCurrency(currentModeledRevenue)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Center Divider with Chevron - Revenue */}
                  <div className="flex flex-col items-center space-y-4">
                    {/* Initiative Type Row - Chevron */}
                    <div className="h-20 flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-system-blue-500/30 to-system-blue-600/20 rounded-full flex items-center justify-center border border-system-blue-400/40 shadow-lg">
                        <svg
                          className="w-5 h-5 text-system-blue-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Revenue Change Indicator - Aligned with revenue row */}
                    <div className="h-20 flex flex-col items-center justify-center">
                      {projectedModel &&
                      projectedModeledRevenue !== currentModeledRevenue ? (
                        <>
                          <div className="text-xs font-medium text-system-gray-400 uppercase tracking-wider">
                            Change
                          </div>
                          <div
                            className={`text-sm font-bold px-3 py-1 rounded-lg shadow-md ${getRevenueChangeStyles(projectedModeledRevenue - currentModeledRevenue)}`}
                          >
                            {projectedModeledRevenue > currentModeledRevenue
                              ? "+"
                              : ""}
                            {toCurrency(
                              projectedModeledRevenue - currentModeledRevenue,
                            )}
                          </div>
                          <div className="text-xs font-medium text-system-gray-400">
                            {(
                              ((projectedModeledRevenue -
                                currentModeledRevenue) /
                                currentModeledRevenue) *
                              100
                            ).toFixed(1)}
                            %
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-system-gray-500">
                          No change
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Side - New Revenue */}
                  <div className="flex-1 space-y-4">
                    {/* New Initiative Type */}
                    {(() => {
                      const config =
                        newValue && !isClearing
                          ? INITIATIVE_TYPE_CONFIGS[newValue]
                          : null
                      return (
                        <div
                          className={`rounded-lg p-4 border h-20 ${config ? `${config.bgColor} ${config.borderColor}` : "bg-system-gray-600/10 border-system-gray-600/20"}`}
                        >
                          <div className="text-center space-y-2">
                            <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                              Initiative Type
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              {config && config.icon}
                              <div
                                className={`text-sm font-medium ${config ? config.color : "text-white"}`}
                              >
                                {isClearing
                                  ? "Not Set"
                                  : config?.label ||
                                    (newValue
                                      ? getInitiativeTypeLabel(newValue)
                                      : "Not Set")}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {/* New Modeled Revenue */}
                    {(() => {
                      const config =
                        newValue && !isClearing
                          ? INITIATIVE_TYPE_CONFIGS[newValue]
                          : null
                      return (
                        <div
                          className={`rounded-lg p-4 border h-20 ${config ? `${config.bgColor} ${config.borderColor}` : "bg-system-gray-600/10 border-system-gray-600/20"}`}
                        >
                          <div className="text-center space-y-2">
                            <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                              Modeled Revenue
                            </div>
                            <div className="text-lg font-bold text-white">
                              {toCurrency(
                                projectedModel
                                  ? projectedModeledRevenue
                                  : currentModeledRevenue,
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Brand Value Tab Content */}
            {activeTab === "brand-value" && hasBrandValueConfig && (
              <div className="space-y-4">
                {/* Brand Value Comparison Headers */}
                <div className="flex items-center justify-between">
                  <div className="flex-1 text-center">
                    <h4 className="text-sm font-medium text-system-gray-300">
                      Current Brand Values
                    </h4>
                  </div>
                  <div className="w-20"></div> {/* Spacer for chevron */}
                  <div className="flex-1 text-center">
                    <h4 className="text-sm font-medium text-system-gray-300">
                      After Change
                    </h4>
                  </div>
                </div>

                {/* Brand Value Comparison */}
                <div className="flex items-center gap-6">
                  {/* Left Side - Current Brand Values */}
                  <div className="flex-1 space-y-4">
                    {/* Current Initiative Type */}
                    <div className="bg-system-purple-600/10 rounded-lg p-4 border border-system-purple-600/20 h-20">
                      <div className="text-center space-y-2">
                        <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                          Initiative Type
                        </div>
                        <div className="text-sm font-medium text-white">
                          {rowData?.initiative_type
                            ? getInitiativeTypeLabel(rowData.initiative_type)
                            : "Not Set"}
                        </div>
                      </div>
                    </div>

                    {/* Current Brand Values by Period */}
                    {brandValuePeriodsWithWeights.map((period: string) => (
                      <div
                        key={`current-brand-${period}`}
                        className="bg-system-purple-600/10 rounded-lg p-4 border border-system-purple-600/20 h-20"
                      >
                        <div className="text-center space-y-2">
                          <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                            {getBrandValueLabel(period)}
                          </div>
                          <div className="text-lg font-bold text-white">
                            {toCurrency(
                              currentBrandValueBreakdown[
                                period as keyof BrandValueBreakdown
                              ],
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Center Divider with Chevron - Brand Value */}
                  <div className="flex flex-col items-center space-y-4">
                    {/* Initiative Type Row - Chevron */}
                    <div className="h-20 flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-system-purple-500/30 to-system-purple-600/20 rounded-full flex items-center justify-center border border-system-purple-400/40 shadow-lg">
                        <svg
                          className="w-5 h-5 text-system-purple-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Brand Value Change Indicators - Aligned with rows */}
                    {brandValuePeriodsWithWeights.map((period: string) => {
                      const currentValue =
                        currentBrandValueBreakdown[
                          period as keyof BrandValueBreakdown
                        ]
                      const projectedValue =
                        projectedBrandValueBreakdown[
                          period as keyof BrandValueBreakdown
                        ]
                      const changeAmount = projectedValue - currentValue

                      return (
                        <div
                          key={`change-${period}`}
                          className="h-20 flex flex-col items-center justify-center"
                        >
                          {changeAmount !== 0 ? (
                            <>
                              <div className="text-xs font-medium text-system-gray-400 uppercase tracking-wider">
                                {period === "180d"
                                  ? "Change"
                                  : `${period} Change`}
                              </div>
                              <div
                                className={`text-sm font-bold px-3 py-1 rounded-lg shadow-md ${getRevenueChangeStyles(changeAmount)}`}
                              >
                                {changeAmount > 0 ? "+" : ""}
                                {toCurrency(changeAmount)}
                              </div>
                              <div className="text-xs font-medium text-system-gray-400">
                                {currentValue > 0
                                  ? (
                                      (changeAmount / currentValue) *
                                      100
                                    ).toFixed(1)
                                  : "∞"}
                                %
                              </div>
                            </>
                          ) : (
                            <div className="text-xs text-system-gray-500">
                              No change
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Right Side - New Brand Values */}
                  <div className="flex-1 space-y-4">
                    {/* New Initiative Type */}
                    {(() => {
                      const config =
                        newValue && !isClearing
                          ? INITIATIVE_TYPE_CONFIGS[newValue]
                          : null
                      return (
                        <div
                          className={`rounded-lg p-4 border h-20 ${config ? `${config.bgColor} ${config.borderColor}` : "bg-system-gray-600/10 border-system-gray-600/20"}`}
                        >
                          <div className="text-center space-y-2">
                            <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                              Initiative Type
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              {config && config.icon}
                              <div
                                className={`text-sm font-medium ${config ? config.color : "text-white"}`}
                              >
                                {isClearing
                                  ? "Not Set"
                                  : config?.label ||
                                    (newValue
                                      ? getInitiativeTypeLabel(newValue)
                                      : "Not Set")}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {/* New Brand Values by Period */}
                    {brandValuePeriodsWithWeights.map((period: string) => {
                      const config =
                        newValue && !isClearing
                          ? INITIATIVE_TYPE_CONFIGS[newValue]
                          : null
                      return (
                        <div
                          key={`projected-brand-${period}`}
                          className={`rounded-lg p-4 border h-20 ${config ? `${config.bgColor} ${config.borderColor}` : "bg-system-gray-600/10 border-system-gray-600/20"}`}
                        >
                          <div className="text-center space-y-2">
                            <div className="text-xs text-system-gray-400 uppercase tracking-wide">
                              {getBrandValueLabel(period)}
                            </div>
                            <div className="text-lg font-bold text-white">
                              {toCurrency(
                                projectedModel
                                  ? projectedBrandValueBreakdown[
                                      period as keyof BrandValueBreakdown
                                    ]
                                  : currentBrandValueBreakdown[
                                      period as keyof BrandValueBreakdown
                                    ],
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 border-t border-system-blue-600/50 bg-system-blue-600/5">
          {/* Campaign cascade choice (campaign rows with child ad sets) */}
          {canCascade && !isClearing && (
            <div className="space-y-2 p-4">
              <p className="text-sm font-medium text-white">
                Apply this initiative type to the campaign&apos;s ad sets?
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cascade-mode"
                    value="all"
                    checked={cascadeMode === "all"}
                    onChange={() => setCascadeMode("all")}
                    className="mt-0.5 w-4 h-4 text-system-pink-500 bg-system-blue-700 border-system-blue-500 focus:ring-system-pink-500 focus:ring-2"
                  />
                  <span>
                    <span className="text-sm font-medium text-white">
                      All ad sets
                    </span>
                    <span className="block text-xs text-system-gray-400">
                      Overwrite every ad set in this campaign, including ones
                      already classified differently.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cascade-mode"
                    value="empty-only"
                    checked={cascadeMode === "empty-only"}
                    onChange={() => setCascadeMode("empty-only")}
                    className="mt-0.5 w-4 h-4 text-system-pink-500 bg-system-blue-700 border-system-blue-500 focus:ring-system-pink-500 focus:ring-2"
                  />
                  <span>
                    <span className="text-sm font-medium text-white">
                      Only unclassified ad sets
                    </span>
                    <span className="block text-xs text-system-gray-400">
                      Fill in ad sets with no initiative type and keep existing
                      assignments unchanged.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Apply to Campaign at Bottom */}
          {isAdset && (
            <div className="space-y-2 p-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="campaign-level"
                  checked={applyCampaignLevel}
                  onChange={e => setApplyCampaignLevel(e.target.checked)}
                  className="w-4 h-4 text-system-pink-500 bg-system-blue-700 border-system-blue-500 rounded focus:ring-system-pink-500 focus:ring-2"
                />
                <label
                  htmlFor="campaign-level"
                  className="text-sm font-medium text-white cursor-pointer"
                >
                  Apply to entire campaign
                </label>
              </div>
              <p className="text-xs text-system-gray-400 ml-7">
                {applyCampaignLevel
                  ? `This will apply the change to all ad sets in the "${campaignName}" campaign`
                  : `This will only apply to the "${itemName}" ad set`}
              </p>
            </div>
          )}

          {/* Warning */}
          {isClearing && (
            <div className="flex items-start gap-3 p-4 bg-system-orange-500/10 border border-system-orange-500/30 rounded-lg">
              <ExclamationTriangleIcon className="w-5 h-5 text-system-orange-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-system-orange-300">
                  Clear Initiative Type
                </p>
                <p className="text-sm text-system-orange-200/80">
                  This will remove the initiative type assignment from{" "}
                  {getScopeDescription()}.
                </p>
              </div>
            </div>
          )}
          {/* Actions */}
          <div className="flex justify-end gap-3 px-4 py-4">
            <Button
              type="button"
              variant={{ variant: "text" }}
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={{ variant: "solid", color: "pink" }}
              onClick={handleConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-sm text-white"
            >
              {(() => {
                if (isLoading) return "Saving..."
                if (isClearing) {
                  return `Clear Initiative Type${getButtonSuffix()}`
                }
                return `Confirm Change${getButtonSuffix()}`
              })()}
            </Button>
          </div>
        </div>
      </div>

      {/* Create/Edit Model Modal */}
      <CreateModelModal
        isOpen={showCreateModel}
        onClose={() => setShowCreateModel(false)}
        initiativeType={newValue as InitiativeType}
        existingModel={(() => {
          if (existingModelForEdit) {
            return {
              id: existingModelForEdit.id as string | null,
              adChannel: existingModelForEdit.adChannel,
              adChannelObjective: existingModelForEdit.adChannelObjective,
              adChannelOptimizationGoal:
                existingModelForEdit.adChannelOptimizationGoal,
              weights: (existingModelForEdit.weights || []).map(w => ({
                ...w,
                modelType: w.modelType as ModelType,
              })),
            }
          }

          const channel = getAdChannel(rowData.media_channel || "")
          if (channel) {
            return {
              id: null,
              adChannel: channel,
              adChannelObjective: rowData.objective as unknown as
                | MetaObjective
                | undefined,
              adChannelOptimizationGoal:
                rowData.optimization_goal as unknown as
                  | CaOptimizationGoal
                  | undefined,
              weights: [],
            }
          }

          return undefined
        })()}
        onSuccess={async () => {
          setShowCreateModel(false)
          // Refetch attribution models to get updated data
          await refetchModels()
        }}
      />
    </div>
  )
}
